/* eslint-disable */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, CardMedia, Typography,
  Button, Chip, InputBase, LinearProgress, Skeleton,
} from '@mui/material';
import { Search, BookOpen, Star } from 'lucide-react';
import Page from '../../components/Page';
import adminServices from '../../services/adminServices';

const CURRENCY = { USD: '$', INR: '₹' };
const sym = (c) => CURRENCY[c] || '₹';

// 2 → 3 → 4 columns, matches the original card design's grid
const CARD_GRID = {
  display: 'grid',
  gap: { xs: 2, sm: 2.5 },
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    sm: 'repeat(3, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
  },
};

// ── status badge — top-left of the cover, same as original (New / Bestseller) ──
function StatusBadge({ type }) {
  if (!type) return null;
  const styles = {
    new: { bg: '#EFF6FF', color: '#1D4ED8' },
    bestseller: { bg: '#FFF1F2', color: '#BE123C' },
  };
  const labels = { new: 'New', bestseller: 'Bestseller' };
  const s = styles[type];
  return (
    <Box sx={{
      position: 'absolute', left: 12, top: 12,
      bgcolor: s.bg, color: s.color,
      fontSize: 12, fontWeight: 600, px: 1.25, py: 0.5, borderRadius: 999,
    }}>
      {labels[type]}
    </Box>
  );
}

// ── price / free tag — same as original ────────────────────────────────────
function PriceTag({ isFree, price, currency }) {
  if (isFree) {
    return (
      <Box sx={{
        display: 'inline-flex', alignItems: 'center',
        bgcolor: '#ECFDF5', color: '#047857',
        fontSize: 12, fontWeight: 600, px: 1.25, py: 0.5, borderRadius: 999,
      }}>
        Free
      </Box>
    );
  }
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      bgcolor: '#FFFBEB', color: '#92400E',
      fontSize: 12, fontWeight: 600, px: 1.25, py: 0.5, borderRadius: 999,
    }}>
      {sym(currency)}{price}
    </Box>
  );
}

// ── category / level tags — same as original ────────────────────────────────
function CategoryTag({ label }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      bgcolor: '#F1F5F9', color: '#475569',
      fontSize: 12, fontWeight: 500, px: 1.25, py: 0.5, borderRadius: 999,
    }}>
      {label}
    </Box>
  );
}

function LevelTag({ label }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      border: '1px solid #E2E8F0', color: '#64748B',
      fontSize: 12, fontWeight: 500, px: 1.25, py: 0.5, borderRadius: 999,
    }}>
      {label}
    </Box>
  );
}

function StarRating({ value }) {
  if (value == null) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: '#64748B' }}>
      <Star size={13} fill="#F59E0B" color="#F59E0B" />
      <span>{Number(value).toFixed(1)}</span>
    </Box>
  );
}

// ── the card — indigo gradient cover, exactly like the original design ─────
function EbookCard({ book, onOpen }) {
  const [imgError, setImgError] = useState(false);
  const p = book.progress;
  const pct = p && p.topicsTotal > 0
    ? Math.round((p.topicsCompleted / p.topicsTotal) * 100)
    : 0;
  const started  = Boolean(p && p.hasStarted);
  const complete = pct === 100 && started;
  const imageSrc = book.coverImageUrl || book.planImageUrl || null;
  const hasImage = Boolean(imageSrc) && !imgError;

  let ctaLabel = book.isFree ? 'Read for free' : 'Buy now';
  if (started) ctaLabel = complete ? 'Read again' : 'Continue';

  return (
    <Card
      onClick={onOpen}
      sx={{
        borderRadius: '16px',
        border: '1px solid #E2E8F0',
        boxShadow: 'none',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'box-shadow .2s ease',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
      }}
    >
      {/* cover */}
      <Box sx={{
        position: 'relative', height: 160,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: hasImage ? 'none' : 'linear-gradient(to bottom right, #6366F1, #4338CA)',
      }}>
        {hasImage ? (
          <CardMedia
            component="img"
            image={imageSrc}
            alt={book.title}
            onError={() => setImgError(true)}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography sx={{
            px: 2, textAlign: 'center', color: 'rgba(255,255,255,.95)',
            fontFamily: 'Georgia, serif', fontSize: 18, lineHeight: 1.3,
          }}>
            {book.title}
          </Typography>
        )}
        <StatusBadge type={book.badge} />
        {started && !hasImage && (
          <Box sx={{
            position: 'absolute', right: 12, top: 12,
            bgcolor: 'rgba(255,255,255,.95)', color: '#4338CA',
            fontSize: 12, fontWeight: 700, px: 1.25, py: 0.5, borderRadius: 999,
          }}>
            {pct}%
          </Box>
        )}
      </Box>

      {/* body */}
      <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, '&:last-child': { pb: 2 } }}>
        <Box>
          <Typography sx={{
            fontFamily: 'Georgia, serif', fontSize: 16, fontWeight: 500, color: '#0F172A',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {book.title}
          </Typography>
          {book.author && (
            <Typography sx={{ fontSize: 13.5, color: '#64748B', mt: 0.25 }}>
              {book.author}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {book.classLevel && <CategoryTag label={book.classLevel} />}
          {book.board && <LevelTag label={book.board} />}
        </Box>

        {started ? (
          <Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 5, borderRadius: 999, bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': { bgcolor: complete ? '#047857' : '#4338CA', borderRadius: 999 },
              }}
            />
            <Typography sx={{ fontSize: 11.5, color: '#64748B', mt: 0.5 }}>
              {p.topicsCompleted}/{p.topicsTotal} topics
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PriceTag isFree={book.isFree} price={book.payableCost} currency={book.currency} />
              <StarRating value={book.rating} />
            </Box>
            {book.topicCount != null && (
              <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                {book.topicCount}p
              </Typography>
            )}
          </Box>
        )}

        <Button
          fullWidth
          disableElevation
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          sx={{
            mt: 0.5, textTransform: 'none', fontWeight: 500, fontSize: 14, borderRadius: '8px',
            bgcolor: '#0F172A', color: '#fff', py: 1,
            '&:hover': { bgcolor: '#1E293B' },
          }}
        >
          {ctaLabel}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── skeleton card ────────────────────────────────────────────────────────────
function CardSk() {
  return (
    <Card sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
      <Skeleton variant="rectangular" height={160} />
      <CardContent sx={{ p: 2 }}>
        <Skeleton width="85%" height={18} />
        <Skeleton width="50%" height={14} sx={{ mt: 0.5 }} />
        <Skeleton variant="rectangular" height={36} sx={{ mt: 1.5, borderRadius: '8px' }} />
      </CardContent>
    </Card>
  );
}

const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'progress', label: 'In progress' },
  { key: 'free',     label: 'Free' },
];

export default function EBookStudentList({ embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const areaBase = location.pathname.startsWith('/auth') ? '/auth' : '/app';

  const [standalonePlans, setStandalonePlans] = useState([]);
  const [bundlePlans, setBundlePlans]         = useState([]);
  const [progress, setProgress]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [search, setSearch]                   = useState('');
  const [filter, setFilter]                   = useState('all');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminServices.getEbookStandalonePlans().catch(() => ({ data: [] })),
      adminServices.getEbookBundlePlans().catch(() => ({ data: [] })),
      adminServices.getUserEbookProgress().catch(() => ({ data: [] })),
    ]).then(([sRes, bRes, pRes]) => {
      setStandalonePlans(Array.isArray(sRes?.data) ? sRes.data : []);
      setBundlePlans(Array.isArray(bRes?.data) ? bRes.data : []);
      setProgress(Array.isArray(pRes?.data) ? pRes.data : []);
    }).finally(() => setLoading(false));
  }, []);

  const progressMap = useMemo(() => {
    const m = {};
    for (const p of progress) m[p.ebookId] = p;
    return m;
  }, [progress]);

  // Matches the real plan shape:
  // { planId, planName, actualCost, payableCost, discount, isFree, imageUrl,
  //   currency, subscriptionType, ebooks: [{ ebookId, title, coverImageUrl,
  //   classLevel, board, isFree, language, publishedAt, chapterCount, topicCount }] }
  const books = useMemo(() => {
    const map = new Map();
    const add = (raw, plan, inBundle) => {
      const id = raw.ebookId ?? raw.contentId;
      if (id == null || map.has(id)) return;
      const publishedRecently = raw.publishedAt
        ? (Date.now() - new Date(raw.publishedAt).getTime()) < 1000 * 60 * 60 * 24 * 21
        : false;
      map.set(id, {
        id,
        title: raw.title || raw.ebookTitle || 'Untitled eBook',
        coverImageUrl: raw.coverImageUrl || null,
        planImageUrl: plan?.imageUrl || null,
        classLevel: raw.classLevel || null,
        board: raw.board || null,
        language: raw.language || null,
        chapterCount: raw.chapterCount ?? null,
        topicCount: raw.topicCount ?? null,
        isFree: Boolean(raw.isFree) || Boolean(plan?.isFree) || plan?.payableCost == null || Number(plan.payableCost) === 0,
        currency: plan?.currency,
        actualCost: plan?.actualCost,
        payableCost: plan?.payableCost,
        discount: plan?.discount || 0,
        inBundle: Boolean(inBundle),
        badge: publishedRecently ? 'new' : null,
        progress: progressMap[id] || null,
      });
    };
    standalonePlans.forEach((pl) => (pl.ebooks || []).forEach((e) => add(e, pl, false)));
    bundlePlans.forEach((pl) =>
      (pl.contents || []).filter((c) => c.contentType === 'EBOOK').forEach((c) => add(c, pl, true)));
    progress.forEach((pr) => add(
      { ebookId: pr.ebookId, title: pr.ebookTitle, coverImageUrl: pr.coverImageUrl, classLevel: pr.classLevel, board: pr.board },
      { isFree: pr.isFree, currency: pr.currency, actualCost: pr.actualCost, payableCost: pr.payableCost, discount: pr.discount },
      false,
    ));
    return [...map.values()];
  }, [standalonePlans, bundlePlans, progress, progressMap]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return books.filter((b) => {
      if (q && !b.title.toLowerCase().includes(q)) return false;
      if (filter === 'progress') return b.progress?.hasStarted;
      if (filter === 'free')     return b.isFree;
      return true;
    });
  }, [books, search, filter]);

  const openBook = (id) => navigate(`${areaBase}/e-book-detail/${id}`);

  const inner = (
    <>
      <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
        <Typography component="h2" sx={{
          fontFamily: 'Georgia, serif', fontSize: { xs: 22, sm: 26 }, fontWeight: 500, color: '#0F172A', mb: 0.5,
        }}>
          Explore E-books
        </Typography>
        <Typography sx={{ fontSize: { xs: 13, sm: 14.5 }, color: '#64748B' }}>
          Read, highlight and track your progress across your subscribed eBooks
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: 13, color: '#64748B', flexGrow: 1 }}>
          {loading ? 'Loading your library…' : `${shown.length} of ${books.length} book${books.length !== 1 ? 's' : ''}`}
        </Typography>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, height: 42,
          bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '10px',
          width: { xs: '100%', sm: 300 },
        }}>
          <Search size={16} color="#94A3B8" />
          <InputBase
            placeholder="Search books…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: 13.5, flexGrow: 1 }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            onClick={() => setFilter(f.key)}
            sx={{
              borderRadius: '999px', fontWeight: 500, fontSize: 13,
              bgcolor: filter === f.key ? '#0F172A' : '#fff',
              color: filter === f.key ? '#fff' : '#64748B',
              border: `1px solid ${filter === f.key ? '#0F172A' : '#E2E8F0'}`,
              '&:hover': { bgcolor: filter === f.key ? '#1E293B' : '#F8FAFC' },
            }}
          />
        ))}
      </Box>

      {loading ? (
        <Box sx={CARD_GRID}>
          {Array.from({ length: 8 }).map((_, i) => <CardSk key={i} />)}
        </Box>
      ) : shown.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 12 }}>
          <BookOpen size={44} color="#CBD5E1" />
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#64748B', mt: 2 }}>
            {search || filter !== 'all' ? 'No books match your filters' : 'No eBooks available yet'}
          </Typography>
          {(search || filter !== 'all') && (
            <Button
              onClick={() => { setSearch(''); setFilter('all'); }}
              sx={{ mt: 1, textTransform: 'none', color: '#0F172A', fontWeight: 500 }}
            >
              Clear filters
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={CARD_GRID}>
          {shown.map((b) => (
            <EbookCard key={b.id} book={b} onOpen={() => openBook(b.id)} />
          ))}
        </Box>
      )}
    </>
  );

  return (
    <PageOrPlain page={!embedded} pageTitle="E-Books">
      <Box sx={{ minHeight: embedded ? '100vh' : '100%', bgcolor: '#F8FAFC' }}>
        <Box sx={{
          maxWidth: 1280, mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          pt: embedded ? { xs: '64px', sm: '80px', md: '96px' } : { xs: 2, sm: 3 },
          pb: { xs: 4, sm: 6 },
        }}>
          {inner}
        </Box>
      </Box>
    </PageOrPlain>
  );
}

function PageOrPlain({ page, pageTitle, children }) {
  if (page) return <Page title={pageTitle}>{children}</Page>;
  return children;
}